import { useCallback } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import {
    gridImageRowsRequestState,
    gridRowImagesState,
} from '~/state/atoms/viewportGridAtoms';
import type { ImageRowsRequest } from '~/viewportGrid';

export interface ImageRowsBinding {
    /** The latest request to show images in a row each. */
    readonly imageRowsRequest: ImageRowsRequest | undefined;
    /** Records the images the rows show. Stable across renders. */
    readonly onRowImagesChanged: (
        seriesInstanceUids: ReadonlyArray<SeriesInstanceUid>
    ) => void;
}

/**
 * Binds a grid host to the shared image-row atoms: Open Datasets writes a
 * request, the host applies it and writes back the images its rows show.
 * The pair maps straight onto the host's `imageRowsRequest` and
 * `onRowImagesChanged` props.
 */
export function useImageRowsBinding(): ImageRowsBinding {
    const imageRowsRequest = useRecoilValue(gridImageRowsRequestState);
    const setRowImages = useSetRecoilState(gridRowImagesState);
    const onRowImagesChanged = useCallback(
        (seriesInstanceUids: ReadonlyArray<SeriesInstanceUid>) =>
            setRowImages(seriesInstanceUids),
        [setRowImages]
    );
    return { imageRowsRequest, onRowImagesChanged };
}
